// src/components/TestCoding.js
import React, { useEffect, useState } from "react";
import { ref, onValue, push, set, get } from "firebase/database";
import { db } from "../services/firebase";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Editor from "@monaco-editor/react";
import { TestCodingVersion } from "../config/testCodingVersion.js";
import "./TestCoding.css";

const TestCoding = ({ user, quizData }) => {
  const { quizId } = useParams();
  const [quiz, setQuiz] = useState(null);
  const [questionKeys, setQuestionKeys] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [codeAnswers, setCodeAnswers] = useState({});
  const [outputs, setOutputs] = useState({});
  const [passedCases, setPassedCases] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [language, setLanguage] = useState("71");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState({});
  const [startTime, setStartTime] = useState(Date.now());
  const [customInput, setCustomInput] = useState("");
  const [customOutput, setCustomOutput] = useState("");
  const [customRunHistory, setCustomRunHistory] = useState([]);
  const [timeElapsed, setTimeElapsed] = useState("00:00");
  const [theme, setTheme] = useState(() => localStorage.getItem("editor-theme") || "vs-dark");
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [timeRestriction, setTimeRestriction] = useState(null);
  const [hasSubmittedBefore, setHasSubmittedBefore] = useState(false);
  const [showDelayedResult, setShowDelayedResult] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem("editor-theme", theme);
  }, [theme]);

  // Check if user has submitted before (for allowReattempt logic)
  useEffect(() => {
    if (quiz && user?.email) {
      const userKey = user.email.replace(/\./g, "_");
      const resultRef = ref(db, `results/`);
      get(resultRef).then((snapshot) => {
        if (snapshot.exists()) {
          const results = snapshot.val();
          const hasSubmitted = Object.values(results).some(result => 
            result.user === user.email && 
            result.testId === `coding-${quiz.topic}-${quiz.date}`
          );
          setHasSubmittedBefore(hasSubmitted);
        }
      });
    }
  }, [quiz, user]);

  // Check time restrictions
  useEffect(() => {
    if (quiz) {
      const now = new Date();
      
      // Check start time
      if (quiz.startTime) {
        const startTime = new Date(quiz.startTime);
        if (now < startTime) {
          setTimeRestriction(`Quiz starts at ${startTime.toLocaleString()}`);
          return;
        }
      }
      
      // Check end time
      if (quiz.endTime) {
        const endTime = new Date(quiz.endTime);
        if (now > endTime) {
          setTimeRestriction(`Quiz ended at ${endTime.toLocaleString()}`);
          return;
        }
      }
      
      setTimeRestriction(null);
    }
  }, [quiz]);

  // Initialize countdown timer
  useEffect(() => {
    if (quiz?.countdown && !timeRestriction && !hasSubmittedBefore) {
      const countdownMinutes = parseInt(quiz.countdown);
      const countdownSeconds = countdownMinutes * 60;
      setTimeRemaining(countdownSeconds);
    }
  }, [quiz, timeRestriction, hasSubmittedBefore]);

  // Countdown timer with auto-submit
  useEffect(() => {
    if (timeRemaining !== null && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            // Auto-submit when time runs out
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeRemaining]);

  useEffect(() => {
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const minutes = String(Math.floor(elapsed / 60)).padStart(2, '0');
      const seconds = String(elapsed % 60).padStart(2, '0');
      setTimeElapsed(`${minutes}:${seconds}`);
    }, 1000);
    return () => clearInterval(timer);
  }, [startTime]);

  const JUDGE0_API = "https://judge0-ce.p.rapidapi.com/submissions?base64_encoded=false&wait=true";
  const RAPID_API_KEY = "ce03d24195mshe8bf56141b4b145p19c2cbjsn01fc831b42c9";

  useEffect(() => {
    if (quizData?.questions) {
      setQuiz(quizData);
      setQuestionKeys(Object.keys(quizData.questions));
    } else if (quizId) {
      const qRef = ref(db, "codingQuestions/" + quizId);
      onValue(qRef, (snapshot) => {
        const data = snapshot.val();
        if (data?.questions) {
          setQuiz(data);
          setQuestionKeys(Object.keys(data.questions));
        }
      });
    }
  }, [quizData, quizId]);

  const handleCodeChange = (val) => {
    setCodeAnswers({ ...codeAnswers, [currentQ]: val });
    localStorage.setItem(`draftCode-${currentQ}`, val);
  };

  const normalize = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, "");
  const tryParse = (str) => {
    try {
      return JSON.stringify(JSON.parse(str));
    } catch {
      return str.trim();
    }
  };

  const runCode = async () => {
    const key = questionKeys[currentQ];
    const q = quiz.questions[key];
    const code = codeAnswers[currentQ] || "";
    if (!code.trim()) return alert("❗ Please enter code first!");

    let passed = 0;
    let testResults = [];
    let logOutput = [];

    setLoading(true);
    setStatus("Running...");

    for (const test of q.testCases) {
      await new Promise((r) => setTimeout(r, 1200));
      try {
        const response = await axios.post(
          JUDGE0_API,
          {
            language_id: Number(language),
            source_code: code,
            stdin: test.input,
          },
          {
            headers: {
              "content-type": "application/json",
              "X-RapidAPI-Key": RAPID_API_KEY,
              "X-RapidAPI-Host": "judge0-ce.p.rapidapi.com",
            },
          }
        );

        const output = (response.data.stdout || "").trim();
        const stderr = (response.data.stderr || "").trim();
        const compile_output = (response.data.compile_output || "").trim();
        const status_desc = response.data.status?.description || "";
        const status_id = response.data.status?.id;

        const expected = test.expectedOutput.trim();
        const passedTest = normalize(tryParse(output)) === normalize(tryParse(expected));

        testResults.push({
          input: test.input,
          expected,
          output,
          passed: passedTest,
        });

        logOutput.push({
          input: test.input,
          stderr,
          compile_output,
          raw_output: output,
          status_desc,
          status_id,
        });

        if (passedTest) passed++;
      } catch (err) {
        if (err.response?.status === 429) {
          alert("⚠️ Judge0 API rate limit exceeded. Please wait and try again.");
        }
        testResults.push({
          input: test.input,
          expected: test.expectedOutput,
          output: "Error",
          passed: false,
        });
        logOutput.push({ input: test.input, error: err.message });
      }
    }

    const score = Math.round((passed / q.testCases.length) * q.marks);
    setOutputs((prev) => ({ ...prev, [currentQ]: testResults }));
    setPassedCases((prev) => ({ ...prev, [currentQ]: score }));
    setLogs((prev) => ({ ...prev, [currentQ]: logOutput }));
    setStatus(`✅ Passed ${passed}/${q.testCases.length} cases, Score: ${score}/${q.marks}`);
    setLoading(false);
  };

  const runCustomTest = async () => {
    const code = codeAnswers[currentQ] || "";
    if (!code.trim()) return alert("❗ Please enter code first!");
    if (!customInput.trim()) return alert("❗ Enter custom input!");

    setLoading(true);
    setCustomOutput("Running...");

    try {
      const response = await axios.post(
        JUDGE0_API,
        {
          language_id: Number(language),
          source_code: code,
          stdin: customInput,
        },
        {
          headers: {
            "content-type": "application/json",
            "X-RapidAPI-Key": RAPID_API_KEY,
            "X-RapidAPI-Host": "judge0-ce.p.rapidapi.com",
          },
        }
      );

      const out = [
        response.data.stdout?.trim(),
        response.data.stderr?.trim(),
        response.data.compile_output?.trim(),
        response.data.status?.description,
      ].filter(Boolean).join("\n");

      const newHistory = [
        { input: customInput, output: out, time: new Date().toLocaleTimeString() },
        ...customRunHistory,
      ];

      setCustomRunHistory(newHistory);
      setCustomOutput(out || "No output returned.");
    } catch (err) {
      setCustomOutput("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetCurrent = () => {
    setCodeAnswers((prev) => ({ ...prev, [currentQ]: "" }));
    setOutputs((prev) => ({ ...prev, [currentQ]: [] }));
    setLogs((prev) => ({ ...prev, [currentQ]: [] }));
    setStatus("");
    setCustomInput("");
    setCustomOutput("");
  };

  const handleSubmit = async () => {
    const duration = Math.round((Date.now() - startTime) / 1000);
    let totalScore = 0;

    const answers = questionKeys.map((key, i) => {
      const q = quiz.questions[key];
      const score = passedCases[i] || 0;
      totalScore += score;
      return {
        question: q.title,
        code: codeAnswers[i] || "",
        testResults: outputs[i] || [],
        logs: logs[i] || [],
        score,
        maxMarks: q.marks,
      };
    });

    const result = {
      user: user?.email || "guest",
      testType: "coding",
      submittedAt: new Date().toISOString(),
      duration: `${duration} sec`,
      testId: `coding-${quiz.topic}-${quiz.date}`,
      quizTitle: quiz.quizTitle,
      topic: quiz.topic,
      score: totalScore,
      maxMarks: questionKeys.reduce((sum, k) => sum + quiz.questions[k].marks, 0),
      answers,
    };

    try {
      await push(ref(db, "results/"), result);
      const userKey = (user?.email || "guest").replace(/\./g, "_");
      await set(ref(db, `leaderboard/${quiz.quizTitle}/${userKey}`), {
        user: user?.email || "guest",
        score: Number(totalScore) || 0,
      });
      localStorage.setItem("latestCodingResult", JSON.stringify(result));
      
      // Handle delayed result display
      if (quiz.showResultAfterSubmit) {
        navigate("/coding-result");
      } else {
        setShowDelayedResult(true);
        const delayMinutes = parseInt(quiz.delayedResultTime) || 30;
        setTimeout(() => {
          setShowDelayedResult(false);
          navigate("/coding-result");
        }, delayMinutes * 60 * 1000);
      }
    } catch (err) {
      console.error("❌ Error saving result:", err);
    }
  };

  // Format time remaining
  const formatTimeRemaining = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (!quiz) return <p>Loading questions...</p>;

  // Show time restriction message
  if (timeRestriction) {
    return (
      <div className="coding-test-container">
        <div className="time-restriction">
          <h2>⏰ Time Restriction</h2>
          <p>{timeRestriction}</p>
          <button onClick={() => navigate("/dashboard")}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  // Show reattempt restriction message
  if (hasSubmittedBefore && !quiz.allowReattempt) {
    return (
      <div className="coding-test-container">
        <div className="reattempt-restriction">
          <h2>🚫 Reattempt Not Allowed</h2>
          <p>You have already submitted this quiz and reattempts are not allowed.</p>
          <button onClick={() => navigate("/dashboard")}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  // Show delayed result message
  if (showDelayedResult) {
    return (
      <div className="coding-test-container">
        <div className="delayed-result">
          <h2>⏳ Results Will Be Available Soon</h2>
          <p>Your quiz has been submitted successfully!</p>
          <p>Results will be available in {quiz.delayedResultTime || 30} minutes.</p>
          <button onClick={() => navigate("/dashboard")}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  const qKey = questionKeys[currentQ];
  const current = quiz.questions[qKey];
  const outputResults = outputs[currentQ] || [];

  return (
    <div className="coding-test-container">
      <div className="coding-header">
        <h2>💻 Coding Test: {quiz.quizTitle}</h2>
        <div className="timer-display">
          ⏱️ Time Elapsed: {timeElapsed}
          {timeRemaining !== null && (
            <span style={{ marginLeft: '20px', color: timeRemaining < 300 ? 'red' : 'inherit' }}>
              ⏰ Time Remaining: {formatTimeRemaining(timeRemaining)}
            </span>
          )}
        </div>
        <div className="quiz-meta">
          <p><strong>Topic:</strong> {quiz.topic}</p>
          {/* {quiz.createdBy && <p><strong>Created By:</strong> {quiz.createdBy}</p>}
          <p><strong>Date:</strong> {quiz.date}</p> */}
          <p><strong>Countdown:</strong> {quiz.countdown} min</p>
          {quiz.startTime && <p><strong>Start Time:</strong> {quiz.startTime}</p>}
          {quiz.endTime && <p><strong>End Time:</strong> {quiz.endTime}</p>}
          {/* <p><strong>Allow Reattempt:</strong> {quiz.allowReattempt ? 'Yes' : 'No'}</p>
          <p><strong>Show Result After Submit:</strong> {quiz.showResultAfterSubmit ? 'Yes' : 'No'}</p>
          {!quiz.showResultAfterSubmit && quiz.delayedResultTime && (
            <p><strong>Delayed Result Time:</strong> {quiz.delayedResultTime} min</p> 
          )}*/}
        </div>
      </div>

      <div className="coding-question">
        <h3>Q{currentQ + 1}: {current.title}</h3>
        <p>{current.description}</p>
        <p><strong>Marks:</strong> {current.marks}</p>

        <label>Choose Language:</label>
        <select value={language} onChange={(e) => setLanguage(e.target.value)}>
          <option value="71">Python 3</option>
          <option value="63">JavaScript</option>
          <option value="54">C++</option>
          <option value="62">Java</option>
          <option value="50">C</option>
        </select>
        <br />

        <h4>📝 Your Code:</h4>

        <label style={{ marginLeft: '1rem' }}>Theme:</label>
        <select
          value={theme}
          onChange={(e) => setTheme(e.target.value)}
          style={{ marginBottom: "1rem" }}
        >   <option value="vs-dark">Dark</option>
          <option value="hc-black">High Contrast Black</option>
          <option value="vs-light">Light</option>
          <option value="hc-light">High Contrast Light</option>
        </select>

        <Editor
          height="300px"
          language={
            language === "71" ? "python" :
              language === "63" ? "javascript" :
                language === "54" ? "cpp" :
                  language === "62" ? "java" : "c"
          }
          theme={theme}
          value={codeAnswers[currentQ] || ""}
          onChange={handleCodeChange}
          options={{ fontSize: 14, minimap: { enabled: false } }}
        />

        <div style={{ marginTop: '1rem' }}>
          <button onClick={runCode} disabled={loading}>
            {loading ? "Running..." : "▶ Run Code"}
          </button>
          <button onClick={resetCurrent} style={{ marginLeft: "1rem" }}>🔄 Reset</button>
        </div>

        {status && <p>Status: <strong>{status}</strong></p>}

        {outputResults.length > 0 && (
          <div className="testcase-results">
            <h4>📋 Test Case Results:</h4>
            <ul>
              {outputResults.map((res, idx) => (
                <li key={idx}>
                  {res.passed ? "✅" : "❌"} <strong>Input:</strong> <code>{res.input}</code><br />
                  <strong>Expected:</strong> <code>{res.expected}</code><br />
                  <strong>Output:</strong> <code>{res.output}</code>
                </li>
              ))}
            </ul>
          </div>
        )}

        <h4>🧪 Try Custom Input</h4>
        <textarea
          rows="4"
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          placeholder="Enter custom input here..."
        />
        <button onClick={runCustomTest} disabled={loading}>Run Custom</button>
        {customOutput && <pre className="output-box">{customOutput}</pre>}

        {customRunHistory.length > 0 && (
          <details>
            <summary>📜 Custom Run History</summary>
            <ul>
              {customRunHistory.map((entry, i) => (
                <li key={i}>
                  <strong>{entry.time}</strong><br />
                  <strong>Input:</strong> {entry.input}<br />
                  <pre>{entry.output}</pre>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>

      <div className="coding-nav-buttons">
        {currentQ > 0 && <button onClick={() => setCurrentQ(currentQ - 1)}>⬅ Prev</button>}
        {currentQ < questionKeys.length - 1 && <button onClick={() => setCurrentQ(currentQ + 1)}>Next ➡</button>}
        {currentQ === questionKeys.length - 1 && <button onClick={handleSubmit}>✅ Submit All</button>}
      </div>

      <footer className="footer">
        <p><strong>{TestCodingVersion.name}</strong></p>
        <p>📦 Version: <strong>{TestCodingVersion.version}</strong> | 📅 Last Updated: {TestCodingVersion.lastUpdated}</p>
        <p>🛠️ Built With: {TestCodingVersion.builtWith}</p>
        <p>🔗 GitHub: <a href={TestCodingVersion.github} target="_blank" rel="noopener noreferrer">{TestCodingVersion.github}</a></p>
      </footer>
    </div>
  );
};

export default TestCoding;